/**
 * Privacy utility functions for masking personal data based on user preferences
 */

import type { UserProfile } from "../api/types";

/**
 * Get the display name for a person, respecting privacy settings
 * @param personName - The actual person name
 * @param personId - The person's ID
 * @param userProfile - Current user's profile with privacy settings
 * @param fallbackName - Optional fallback (e.g., vanity_name) if personName is empty
 * @returns Either the real name, fallback, or "Demoperson {id}" if privacy mode is enabled
 */
export function getDisplayName(
	personName: string,
	personId: number,
	userProfile?: UserProfile,
	fallbackName?: string | null
): string {
	if (userProfile?.hide_personal_data) {
		return `Demoperson ${personId}`;
	}
	// Use personName if not empty, otherwise fall back to fallbackName
	const displayName = personName?.trim() || fallbackName || "";
	return displayName || `Person ${personId}`;
}

/**
 * Get the display name for a company, respecting privacy settings
 * @param companyName - The actual company name
 * @param companyId - The company's ID
 * @param userProfile - Current user's profile with privacy settings
 * @returns Either the real name or "Company {id}" if privacy mode is enabled
 */
export function getDisplayCompanyName(
	companyName: string,
	companyId: number,
	userProfile?: UserProfile
): string {
	if (userProfile?.hide_personal_data) {
		return `Company ${companyId}`;
	}
	return companyName;
}
